/**
 * The app: providers, then the route table. Every screen is a DYNAMIC import at the route level
 * (docs/00-project-rules/07 §4) — one page must never pull the whole system into the first load.
 *
 * `/login` is the only screen outside the shell; everything under the shell needs a session.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { AppShell } from './layout/AppShell.js';
import { createQueryClient } from './services/queryClient.js';

const LoginPage = lazy(() => import('./features/auth/LoginPage.js'));
const ChangePasswordPage = lazy(() => import('./features/auth/ChangePasswordPage.js'));
const OverviewPage = lazy(() => import('./features/overview/OverviewPage.js'));
const MovementsPage = lazy(() => import('./features/movements/MovementsPage.js'));
const TerminalSharePage = lazy(() => import('./features/terminalShare/TerminalSharePage.js'));
const AgentSharePage = lazy(() => import('./features/agentShare/AgentSharePage.js'));
const PortProfilePage = lazy(() => import('./features/portProfile/PortProfilePage.js'));
const AgentProfilePage = lazy(() => import('./features/agentProfile/AgentProfilePage.js'));
const ShipsPage = lazy(() => import('./features/ships/ShipsPage.js'));
const StatsPage = lazy(() => import('./features/generalStats/StatsPage.js'));
const ProspectsPage = lazy(() => import('./features/prospects/ProspectsPage.js'));
const RegistryPage = lazy(() => import('./features/registry/RegistryPage.js'));
const RegistryShipPage = lazy(() => import('./features/registry/RegistryShipPage.js'));
const FleetPage = lazy(() => import('./features/fleet/FleetPage.js'));
const FleetVesselPage = lazy(() => import('./features/fleet/FleetVesselPage.js'));
/*
 * Bản đồ (docs/14-vessel-map): import động giữ MapLibre (~292 KB gzip, ĐO 13/09/2026) ngoài
 * lần tải đầu — không màn nào khác kéo engine theo.
 */
const VesselMapPage = lazy(() => import('./features/vesselMap/VesselMapPage.js'));
const ScrapingPage = lazy(() => import('./features/adminScraping/ScrapingPage.js'));
const TerminalsPage = lazy(() => import('./features/adminTerminals/TerminalsPage.js'));
const TerminalGroupsPage = lazy(() => import('./features/adminGroups/TerminalGroupsPage.js'));
const CargoGroupsPage = lazy(() => import('./features/adminCargo/CargoGroupsPage.js'));
const AgentsPage = lazy(() => import('./features/adminAgents/AgentsPage.js'));
const DataQualityPage = lazy(() => import('./features/adminQuality/DataQualityPage.js'));
const BerthCoordsPage = lazy(() => import('./features/adminBerthCoords/BerthCoordsPage.js'));
const BerthLinksPage = lazy(() => import('./features/adminBerthLinks/BerthLinksPage.js'));
const BerthCatalogPage = lazy(() => import('./features/adminBerthCatalog/BerthCatalogPage.js'));
const RegistryAdminPage = lazy(() => import('./features/adminRegistry/RegistryAdminPage.js'));
const FleetAdminPage = lazy(() => import('./features/adminFleet/FleetAdminPage.js'));
const UsersPage = lazy(() => import('./features/adminUsers/UsersPage.js'));
const ApiKeysPage = lazy(() => import('./features/adminApiKeys/ApiKeysPage.js'));

const Loading = <p className="p-6 text-[13px] text-muted-foreground">Đang tải…</p>;

export function App() {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={Loading}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/*
              `/map` nằm NGOÀI shell, cùng hạng với `/login`: Live Tracking Vessel tràn hết màn
              hình, không thanh trên, không thanh bên (chủ dự án chốt 13/09/2026). Màn đó tự gác
              phiên đăng nhập vì không còn `AppShell` làm việc ấy hộ.
            */}
            <Route path="/map" element={<VesselMapPage />} />
            <Route element={<AppShell />}>
              <Route index element={<OverviewPage />} />
              <Route path="/movements" element={<MovementsPage />} />
              <Route path="/share/terminals" element={<TerminalSharePage />} />
              <Route path="/share/agents" element={<AgentSharePage />} />
              <Route path="/ships" element={<ShipsPage />} />
              <Route path="/ports" element={<PortProfilePage />} />
              <Route path="/agents" element={<AgentProfilePage />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/prospects" element={<ProspectsPage />} />
              <Route path="/registry" element={<RegistryPage />} />
              <Route path="/registry/:vrNo" element={<RegistryShipPage />} />
              <Route path="/fleet" element={<FleetPage />} />
              <Route path="/fleet/:imo" element={<FleetVesselPage />} />
              <Route path="/admin/scraping" element={<ScrapingPage />} />
              <Route path="/admin/terminals" element={<TerminalsPage />} />
              <Route path="/admin/terminal-groups" element={<TerminalGroupsPage />} />
              <Route path="/admin/cargo-groups" element={<CargoGroupsPage />} />
              <Route path="/admin/agents" element={<AgentsPage />} />
              <Route path="/admin/data-quality" element={<DataQualityPage />} />
              <Route path="/admin/berth-coords" element={<BerthCoordsPage />} />
              <Route path="/admin/berth-links" element={<BerthLinksPage />} />
              <Route path="/admin/berth-catalog" element={<BerthCatalogPage />} />
              <Route path="/admin/registry" element={<RegistryAdminPage />} />
              <Route path="/admin/fleet" element={<FleetAdminPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/api-keys" element={<ApiKeysPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
